import { cn } from '@/lib/cn'
import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

interface Props {
  label: string
  value: ReactNode
  sub?: ReactNode
  icon?: LucideIcon
  // When true, the card draws a gold accent ring so the eye lands on
  // metrics the user has to act on (e.g. pending approvals > 0).
  alert?: boolean
  className?: string
}

// KPICard is a tight dashboard stat card on the AMA brand surface.
// Hierarchy comes from weight/spacing; `alert` adds a gold highlight.
export function KPICard({ label, value, sub, icon: Icon, alert, className }: Props) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-border bg-card p-4 shadow-card transition-all duration-200 hover:shadow-card-hover',
        alert && 'ring-1 ring-accent',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        {Icon ? (
          <Icon
            className={cn('h-4 w-4', alert ? 'text-accent' : 'text-muted-foreground')}
            aria-hidden="true"
          />
        ) : null}
      </div>
      <p className="mt-2 text-2xl font-bold tabular-nums text-foreground">{value}</p>
      {sub ? <p className="mt-1 text-xs text-muted-foreground">{sub}</p> : null}
    </div>
  )
}
